
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchShortcut } from '@/hooks/useSearchShortcut';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty } from "@/components/ui/command";
import { Trek } from '@/lib/types/trek-types';
import { useDebounce } from '@/hooks/useDebounce';
import { searchTreks, getSearchSuggestions } from '@/lib/services/search-service';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

const SearchPortal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [results, setResults] = useState<Trek[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const navigate = useNavigate();
  const analytics = useAnalytics();

  useSearchShortcut({
    onActivate: () => {
      setIsOpen(true);
      analytics.track('Global Search Activated', { method: 'keyboard_shortcut' });
    },
    isActive: isOpen
  });

  // Close on escape
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Search functionality
  useEffect(() => {
    const search = async () => {
      if (debouncedSearchTerm.length < 2) {
        setSuggestions([]);
        setResults([]);
        return;
      }

      setIsLoading(true);

      try {
        // Fetch suggestions
        const suggestionResults = await getSearchSuggestions(debouncedSearchTerm);
        setSuggestions(suggestionResults);

        // Fetch trek results
        const trekResults = await searchTreks(debouncedSearchTerm);
        setResults(trekResults.slice(0, 5));

        analytics.track('Global Search', {
          query: debouncedSearchTerm,
          results_count: trekResults.length
        });
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    search();
  }, [debouncedSearchTerm, analytics]);

  const handleSuggestionSelect = (suggestion: string) => {
    analytics.track('Search Suggestion Selected', { suggestion });
    navigate(`/search?q=${encodeURIComponent(suggestion)}`);
    setIsOpen(false);
  };

  const handleResultSelect = (trekId: string) => {
    analytics.track('Search Result Selected', { trek_id: trekId });
    navigate(`/trek/${trekId}`);
    setIsOpen(false);
  };

  const handleViewAllResults = () => {
    if (!searchTerm.trim()) return;
    
    analytics.track('View All Search Results', { query: searchTerm });
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    setIsOpen(false);
  };

  // Highlight the matching part of the suggestion
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.substring(0, index)}
        <span className="font-bold underline">
          {text.substring(index, index + query.length)}
        </span>
        {text.substring(index + query.length)}
      </>
    );
  };

  // Close dialog when backdrop is clicked
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      analytics.track('Global Search Closed', { method: 'backdrop_click' });
    }
  };

  return createPortal(
    <CommandDialog open={isOpen} onOpenChange={handleOpenChange}>
      <Command className="rounded-lg border shadow-md">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Search for treks..."
            autoFocus
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <CommandList>
          {isLoading && (
            <div className="py-6 text-center">
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2"></div>
              <span className="text-sm text-muted-foreground">Searching...</span>
            </div>
          )}

          {!isLoading && debouncedSearchTerm.length > 1 && suggestions.length === 0 && results.length === 0 && (
            <CommandEmpty className="py-6 text-center text-sm">
              No results found for "{debouncedSearchTerm}"
            </CommandEmpty>
          )}

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="py-2 px-2">
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                Suggestions
              </div>
              <ul>
                {suggestions.map((suggestion, index) => (
                  <li
                    key={`suggestion-${index}`}
                    className="px-2 py-1.5 text-sm rounded-md cursor-pointer flex items-center hover:bg-muted"
                    onClick={() => handleSuggestionSelect(suggestion)}
                  >
                    <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                    {highlightMatch(suggestion, debouncedSearchTerm)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Trek Results */}
          {results.length > 0 && (
            <div className="py-2 px-2">
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                Trek Results
              </div>
              <ul>
                {results.map((trek) => (
                  <li
                    key={trek.id}
                    className="px-2 py-2 text-sm rounded-md cursor-pointer hover:bg-muted"
                    onClick={() => handleResultSelect(trek.id)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-md overflow-hidden">
                        <img
                          src={trek.images[0]}
                          alt={trek.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="font-medium">{highlightMatch(trek.name, debouncedSearchTerm)}</p>
                        <p className="text-xs text-muted-foreground">{trek.location} • {trek.difficulty}</p>
                      </div>
                    </div>
                  </li>
                ))}
                <li 
                  className="mt-2 px-2 py-1.5 text-sm text-center text-primary font-medium rounded-md cursor-pointer hover:bg-muted"
                  onClick={handleViewAllResults}
                >
                  View all results
                </li>
              </ul>
            </div>
          )}
        </CommandList>
      </Command>
    </CommandDialog>,
    document.body
  );
};

export default SearchPortal;
